import React, { useEffect } from 'react';

import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { MultiSelect } from 'primereact/multiselect';
import { useForm, Controller } from 'react-hook-form';

import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { generateFullName } from 'utils/generateFullNameUtils';
import { useFileManagerContext } from 'modules/file_manager/context/FileManagerContext';
import { getFileClients, shareFile } from 'services/fileManager/fileManagerServices';
import { ModalHeader } from 'shared/ModalHeader';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';

function ShareModal() {
  const { modals, isLeadFileManager, isCaseFileManager, selectedItem, caseDetails, selectedRows, setSelectedRows, setFileManagerFilters } =
    useFileManagerContext();
  const { showShareFileModal, setShowShareFileModal } = modals;
  const { addToast } = useToast();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const [clientOptions, setClientOptions] = React.useState([]);
  const [sharedContacts, setSharedContacts] = React.useState([]);

  // Check if we have folders selected
  const hasFolders = selectedRows?.some((item) => item.entry_type === 'folder');
  const hasFiles = selectedRows?.some((item) => item.entry_type === 'file');
  const onlyFolders = hasFolders && !hasFiles;
  const mixedSelection = hasFolders && hasFiles;

  useEffect(() => {
    let caseId = selectedRows?.[0]?.case_id ? selectedRows?.[0]?.case_id : '';
    fetchClients(caseId);
  }, [selectedRows]);

  useEffect(() => {
    if (selectedRows && selectedRows.length > 0 && clientOptions.length > 0) {
      const defaultAssigneeList = selectedRows.reduce((acc, row) => {
        if (row.file_shared_contact_id) {
          row.file_shared_contact_id.forEach((fileContactId) => {
            const foundClient = clientOptions.find((client) => client.contact_id === fileContactId);
            if (foundClient) {
              acc.push({
                ...foundClient,
                display_name: row.display_name,
              });
            }
          });
        }
        return acc;
      }, []);
      setSharedContacts(defaultAssigneeList);
    }
  }, [clientOptions, selectedRows]);

  const fetchClients = (caseId = '') => {
    let obj = {
      case_id: caseId,
    };
    getFileClients(obj)
      .then((response) => {
        if (response && response.data && response.data.length) {
          let data = [];
          response.data.forEach((element) => {
            let label = generateFullName(element);
            data.push({
              label: label,
              value: element?.client_cognito_username ? element?.client_cognito_username : element?.contact_id,
              email: element?.email,
              is_create_client_portal: element?.is_create_client_portal,
              contact_id: element?.contact_id ? element?.contact_id : '',
              file_name: element?.display_name ? element?.display_name : '',
            });
          });
          setClientOptions([...data]);
        } else {
          setClientOptions([]);
        }
      })
      .catch(() => {
        setClientOptions([]);
      });
  };

  const shareItemsWithClient = (data) => {
    if (selectedRows?.length > 0) {
      // Separate files and folders
      const files = selectedRows.filter((item) => item.entry_type === 'file');
      const folders = selectedRows.filter((item) => item.entry_type === 'folder');

      if (folders.length > 0 || files.length > 0) {
        // Create unified sharing data with both files and folders
        let unifiedShareData = {
          client_cognito_username: data.assignee,
        };

        // Add file list if there are files
        if (files.length > 0) {
          unifiedShareData.file_list = files.map((row) => ({ file_pk: row.pk, file_sk: row.sk }));
        }

        // Add folder list if there are folders
        if (folders.length > 0) {
          unifiedShareData.folder_list = folders.map((row) => ({ folder_pk: row.pk, folder_sk: row.sk }));
        }

        let shareObj = {
          case_id: selectedRows[0]?.case_id,
          data: unifiedShareData,
          is_lead: isLeadFileManager,
        };

        // Use single shareFile call for both files and folders
        shareFile(shareObj)
          .then(() => {
            const itemType = onlyFolders ? 'FOLDERS' : mixedSelection ? 'ITEMS' : 'FILES';
            addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, `${itemType} shared successfully`);
            setShowShareFileModal(false);
            setSelectedRows([]);
            setFileManagerFilters((prev) => ({ ...prev, refresh: true }));
          })
          .catch((error) => {
            console.log(error);
            const itemType = onlyFolders ? 'FOLDERS' : mixedSelection ? 'ITEMS' : 'FILES';
            addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, `Failed to share ${itemType.toLowerCase()}`);
          });
      }
    }
  };

  function checkOptionDisabled(index) {
    if (index.email === '' || !index.is_create_client_portal) {
      return true;
    }
    return false;
  }

  function ClientsOptionTemplate(e) {
    return (
      <div>
        {e.label} {!e.email ? '( Email address not found )' : !e.is_create_client_portal ? '( Lawft Access Disabled )' : ''}
      </div>
    );
  }

  const customFooter = () => {
    return (
      <div className="footer-btn">
        <Button label="Cancel" onClick={() => setShowShareFileModal(false)} className="p-button p-button-secondary me-3 outline" />
        <Button label="Share" className="p-button p-button-secondary" onClick={handleSubmit(shareItemsWithClient)} autoFocus />
      </div>
    );
  };

  const getModalTitle = () => {
    if (onlyFolders) {
      return selectedRows.length === 1 ? 'Share Folder' : 'Share Folders';
    } else if (mixedSelection) {
      return 'Share Items';
    } else {
      return selectedRows.length === 1 ? 'Share File' : 'Share Files';
    }
  };

  const getItemDescription = () => {
    const folderCount = selectedRows.filter((item) => item.entry_type === 'folder').length;
    const fileCount = selectedRows.filter((item) => item.entry_type === 'file').length;

    let description = '';
    if (folderCount > 0) {
      description += `${folderCount} folder${folderCount !== 1 ? 's' : ''}`;
    }
    if (fileCount > 0) {
      if (description) description += ' and ';
      description += `${fileCount} file${fileCount !== 1 ? 's' : ''}`;
    }
    return description;
  };

  return (
    <Dialog
      className="new-task-popup"
      onHide={() => setShowShareFileModal(false)}
      header={ModalHeader}
      footer={customFooter}
      title={getModalTitle()}
      visible={showShareFileModal}
      style={{ width: '40vw' }}
    >
      <div className="mb-3 p-medium">
        <span>
          Sharing {getItemDescription()} with selected contacts
          {hasFolders && (
            <div className="text-muted small mt-2">
              <i className="fas fa-info-circle me-1"></i>
              Note: Sharing folders will also share all files and subfolders within them.
            </div>
          )}
        </span>
      </div>

      <div className="d-flex align-items-center flex-wrap">
        <div className="col-lg-4 col-12 p-medium">Select Contact (s)</div>
        <div className="col-lg-8 col-12 select-tag-input">
          <Controller
            name="assignee"
            control={control}
            defaultValue={[]}
            rules={{ required: true }}
            render={({ field }) => (
              <>
                <MultiSelect
                  options={clientOptions}
                  onChange={(e) => field.onChange(e.value)}
                  placeholder="Select Contact (s)"
                  className="input-shadow w-100 F-size14"
                  filter
                  id={field.name}
                  value={field.value}
                  optionLabel="label"
                  optionValue="value"
                  display="chip"
                  emptyFilterMessage="No records found"
                  optionDisabled={(index) => checkOptionDisabled(index)}
                  itemTemplate={ClientsOptionTemplate}
                />
              </>
            )}
          />
          {errors?.assignee && <InputErrorMessage>Required</InputErrorMessage>}
        </div>

        <div className="mt-3">
          {selectedRows?.map((item, index) => (
            <div key={index}>
              <div className="p-medium d-flex align-items-center">
                {item.entry_type === 'folder' ? (
                  <i className="far fa-folder me-2 text-warning"></i>
                ) : (
                  <i className="far fa-file me-2 text-primary"></i>
                )}
                <span>
                  {item.entry_type === 'folder' ? 'Folder' : 'File'}: {item.display_name}
                </span>
              </div>
              <div className="col-12 mb-3 mt-2">
                {sharedContacts
                  .filter((contact) => contact.display_name === item.display_name)
                  .map((assignee, idx) => (
                    <span key={idx} className="badge bg-secondary mx-1">
                      {assignee.label}
                    </span>
                  ))}
                {sharedContacts.filter((contact) => contact.display_name === item.display_name).length === 0 && (
                  <div className="d-flex align-items-center">
                    <i className="icon icon-Alert me-2 cursor-pointer F-size16"></i>
                    <span className="text-muted">{item.entry_type === 'folder' ? 'Folder' : 'File'} not shared</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

export default ShareModal;
